/**
 * 원격지원 파일 주고받기 — P2P 프로토콜(DataChannel 'fx')의 순수 함수
 *
 * ⚠ import를 넣지 말 것 — scripts/test-remote-file.js가 typescript.transpileModule로 통째로 평가한다.
 *
 * 데이터는 P2P로만 흐르고 서버는 보지 않는다. 서버는 허가(rs:file:start)와 감사(rs:file:result)만 맡는다.
 *  - 제어 메시지: JSON 문자열(parseFxMessage가 형식·길이를 검증하고 모르는 것은 버린다)
 *  - 데이터: 바이너리. 앞 4바이트 = 파일 번호(u32 BE), 나머지 = 내용
 *  - id(묶음·파일·요청)는 역할별 홀짝으로 나눈다 — 담당자 홀수, 직원 짝수. 그래서 양방향 동시 전송에서도
 *    id 하나가 어느 쪽 묶음인지 정확히 가리킨다
 */

export const FX_CHUNK = 64 * 1024;
/** 파일당 최대 크기. Rust(MAX_RS_FILE_BYTES)·서버(rs:file:start)가 같은 값을 따로 강제한다 */
export const FX_MAX_FILE_BYTES = 2 * 1024 * 1024 * 1024;
export const FX_MAX_FILES = 20;
/** 받는 쪽이 디스크에 썼다고 확인(ack)하지 않은 바이트 상한 — 받는 쪽 메모리 상한을 겸한다 */
export const FX_WINDOW = 8 * 1024 * 1024;
/** 송신 버퍼 상한 — 넘으면 bufferedamountlow까지 기다린다(입력 채널 'ev'가 밀리지 않게 작게 둔다) */
export const FX_BUFFER_HIGH = 1024 * 1024;
export const FX_BUFFER_LOW = 256 * 1024;
/** 받는 쪽이 모아서 한 번에 쓰는 단위 */
export const FX_WRITE_BATCH = 1024 * 1024;
/** 보내는 쪽이 파일에서 한 번에 읽는 단위(청크로 다시 쪼갠다) */
export const FX_READ_BLOCK = 1024 * 1024;
/** 직원 동의 대기 */
export const FX_CONSENT_MS = 60_000;
/** 파일 요청(pull) 대기 — 직원이 파일 선택 창에서 고르는 시간까지 포함 */
export const FX_PULL_MS = 5 * 60_000;
export const FX_NAME_MAX = 255;
export const FX_MSG_MAX = 16 * 1024;
const MAX_ID = 0x7fffffff;

export type FxRole = 'host' | 'operator';

export interface FxFileMeta {
  fid: number;
  name: string;
  size: number;
}

export type FxMessage =
  | { t: 'offer'; bid: number; files: FxFileMeta[]; pullId?: number }
  | { t: 'answer'; bid: number; accept: boolean }
  | { t: 'pull'; pid: number }
  | { t: 'pull-declined'; pid: number }
  | { t: 'begin'; fid: number }
  | { t: 'end'; fid: number }
  | { t: 'ack'; fid: number; bytes: number }
  | { t: 'done'; fid: number; ok: boolean; savedAs?: string }
  | { t: 'cancel'; bid: number; reason: string };

/** 역할별 첫 id — 담당자 1(홀수), 직원 2(짝수). 다음 id는 +2 */
export function firstId(role: FxRole): number {
  return role === 'operator' ? 1 : 2;
}

/** 이 id를 그 역할이 만들었는가 */
export function isIdOf(role: FxRole, id: number): boolean {
  return Number.isInteger(id) && id > 0 && id % 2 === (role === 'operator' ? 1 : 0);
}

function isId(v: unknown): v is number {
  return typeof v === 'number' && Number.isInteger(v) && v > 0 && v <= MAX_ID;
}

function isSize(v: unknown): v is number {
  return typeof v === 'number' && Number.isInteger(v) && v >= 0 && v <= FX_MAX_FILE_BYTES;
}

/** 표시·저장에 쓸 수 있는 이름인가(경로 구분자는 받는 쪽 Rust가 다시 정리한다) */
export function cleanFileName(name: unknown): string | null {
  if (typeof name !== 'string') return null;
  // 제어문자 제거 + 앞뒤 공백
  // eslint-disable-next-line no-control-regex
  const s = name.replace(/[\u0000-\u001f\u007f]/g, '').trim();
  if (!s || s.length > FX_NAME_MAX) return null;
  return s;
}

/** DataChannel 문자열 메시지 → 검증된 메시지. 모르는 것·깨진 것은 null */
export function parseFxMessage(raw: unknown): FxMessage | null {
  if (typeof raw !== 'string' || raw.length === 0 || raw.length > FX_MSG_MAX) return null;
  let m: any;
  try {
    m = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!m || typeof m !== 'object') return null;
  switch (m.t) {
    case 'offer': {
      if (!isId(m.bid) || !Array.isArray(m.files)) return null;
      if (m.files.length === 0 || m.files.length > FX_MAX_FILES) return null;
      const files: FxFileMeta[] = [];
      const seen = new Set<number>();
      for (const f of m.files) {
        const name = cleanFileName(f?.name);
        if (!f || !isId(f.fid) || seen.has(f.fid) || !name || !isSize(f.size)) return null;
        seen.add(f.fid);
        files.push({ fid: f.fid, name, size: f.size });
      }
      if (m.pullId !== undefined && !isId(m.pullId)) return null;
      return m.pullId !== undefined ? { t: 'offer', bid: m.bid, files, pullId: m.pullId } : { t: 'offer', bid: m.bid, files };
    }
    case 'answer':
      return isId(m.bid) && typeof m.accept === 'boolean' ? { t: 'answer', bid: m.bid, accept: m.accept } : null;
    case 'pull':
    case 'pull-declined':
      return isId(m.pid) ? { t: m.t, pid: m.pid } : null;
    case 'begin':
    case 'end':
      return isId(m.fid) ? { t: m.t, fid: m.fid } : null;
    case 'ack':
      return isId(m.fid) && isSize(m.bytes) ? { t: 'ack', fid: m.fid, bytes: m.bytes } : null;
    case 'done': {
      if (!isId(m.fid) || typeof m.ok !== 'boolean') return null;
      const savedAs = m.savedAs === undefined ? undefined : cleanFileName(m.savedAs);
      if (m.savedAs !== undefined && !savedAs) return null;
      return savedAs ? { t: 'done', fid: m.fid, ok: m.ok, savedAs } : { t: 'done', fid: m.fid, ok: m.ok };
    }
    case 'cancel': {
      if (!isId(m.bid)) return null;
      const reason = typeof m.reason === 'string' && /^[a-z_]{1,32}$/.test(m.reason) ? m.reason : 'canceled';
      return { t: 'cancel', bid: m.bid, reason };
    }
    default:
      return null;
  }
}

/** 데이터 청크 = [fid u32 BE][내용] */
export function encodeChunk(fid: number, data: Uint8Array): ArrayBuffer {
  const out = new Uint8Array(4 + data.byteLength);
  new DataView(out.buffer).setUint32(0, fid >>> 0, false);
  out.set(data, 4);
  return out.buffer;
}

export function decodeChunk(buf: ArrayBuffer): { fid: number; data: Uint8Array } | null {
  if (!(buf instanceof ArrayBuffer) || buf.byteLength < 4 || buf.byteLength > 4 + FX_CHUNK) return null;
  const fid = new DataView(buf).getUint32(0, false);
  if (!isId(fid)) return null;
  return { fid, data: new Uint8Array(buf, 4) };
}

export type OutgoingCheck =
  | { ok: true }
  | { ok: false; reason: 'empty' | 'too_many' | 'too_large' | 'bad_name'; name?: string };

/** 보내기 전 검사 — 받는 쪽·서버·Rust가 같은 상한을 다시 강제한다 */
export function checkOutgoing(files: { name: string; size: number }[]): OutgoingCheck {
  if (files.length === 0) return { ok: false, reason: 'empty' };
  if (files.length > FX_MAX_FILES) return { ok: false, reason: 'too_many' };
  for (const f of files) {
    if (!cleanFileName(f.name)) return { ok: false, reason: 'bad_name', name: String(f.name) };
    if (!isSize(f.size)) return { ok: false, reason: 'too_large', name: f.name };
  }
  return { ok: true };
}

/** 'setup.exe' / 'setup.exe 외 2개' */
export function summarizeFiles(files: { name: string }[]): string {
  if (files.length === 0) return '';
  return files.length === 1 ? files[0].name : `${files[0].name} 외 ${files.length - 1}개`;
}

/** 진행률 0~100(정수). 0바이트 묶음은 끝나야 100 */
export function percentOf(done: number, total: number): number {
  if (total <= 0) return 0;
  return Math.max(0, Math.min(100, Math.floor((done / total) * 100)));
}

/**
 * 전송 속도(바이트/초) — 최근 3초 구간의 평균. 진행 막대 옆 '12.3MB/s · 8초 남음'에 쓴다
 */
export function createRateMeter(windowMs = 3000) {
  const samples: { t: number; bytes: number }[] = [];
  return {
    push(totalBytes: number, now: number) {
      samples.push({ t: now, bytes: totalBytes });
      while (samples.length > 2 && now - samples[0].t > windowMs) samples.shift();
    },
    rate(): number {
      if (samples.length < 2) return 0;
      const a = samples[0];
      const b = samples[samples.length - 1];
      const dt = b.t - a.t;
      return dt > 0 ? Math.max(0, ((b.bytes - a.bytes) * 1000) / dt) : 0;
    },
  };
}

/** 남은 시간(초). 속도를 모르면 null */
export function etaSeconds(remainingBytes: number, bytesPerSec: number): number | null {
  if (!(bytesPerSec > 0) || remainingBytes <= 0) return null;
  return Math.ceil(remainingBytes / bytesPerSec);
}

/** 사유 코드 → 사람에게 보일 문구(양쪽 화면 공용) */
export function fxReasonLabel(reason: string | undefined): string {
  switch (reason) {
    case 'declined':
      return '받지 않기로 했습니다';
    case 'timeout':
      return '응답이 없어 취소했습니다';
    case 'busy':
      return '다른 파일을 주고받는 중이라 받지 않았습니다';
    case 'policy_off':
      return '관리자가 파일 주고받기를 막았습니다';
    case 'server_denied':
      return '서버가 허용하지 않았습니다';
    case 'write_failed':
      return '파일을 저장하지 못했습니다';
    case 'disk_full':
      return '저장 공간이 부족합니다';
    case 'read_failed':
      return '파일을 읽지 못했습니다';
    case 'size_mismatch':
      return '받은 크기가 맞지 않습니다';
    case 'disconnected':
      return '연결이 끊겼습니다';
    case 'canceled':
    default:
      return '취소했습니다';
  }
}
